"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Loader, CheckCheck, CircleAlert } from "lucide-react";
const inputClass = "peer block w-full appearance-none border-0 border-b border-border bg-transparent px-0 py-3 text-base text-foreground focus:border-primary focus:outline-none focus:ring-0 placeholder:text-transparent focus:placeholder:text-muted-foreground/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-mono";
const labelClass = "absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 transform text-sm text-muted-foreground uppercase tracking-wider duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:text-primary font-mono";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="221604" className="flex flex-col items-center justify-center space-y-4 py-12 text-center animate-in fade-in duration-500">
        <div className="rounded-full bg-primary/10 p-3">
          <CheckCheck className="size-8 text-primary" />
        </div>
        <h3 className="font-default text-2xl font-semibold text-foreground">
          Order Received
        </h3>
        <p className="text-muted-foreground font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary/10">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8 mt-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem className="relative z-0 w-full">
            <FormControl>
              <input {...field} className={inputClass} placeholder="Full Name" />
            </FormControl>
            <FormLabel className={labelClass}>Full Name</FormLabel>
            <FormMessage className="absolute -bottom-5 text-xs font-mono" />
          </FormItem>} />

      <FormField control={control} name="acres" rules={{
      required: "Acreage is required"
    }} render={({
      field
    }) => <FormItem className="relative z-0 w-full">
            <FormControl>
              <input {...field} type="text" className={inputClass} placeholder="e.g. 450" />
            </FormControl>
            <FormLabel className={labelClass}>Total Acreage</FormLabel>
            <FormMessage className="absolute -bottom-5 text-xs font-mono" />
          </FormItem>} />

      <FormField control={control} name="location" rules={{
      required: "County/State is required"
    }} render={({
      field
    }) => <FormItem className="relative z-0 w-full">
            <FormControl>
              <input {...field} className={inputClass} placeholder="County, State" />
            </FormControl>
            <FormLabel className={labelClass}>County & State</FormLabel>
            <FormMessage className="absolute -bottom-5 text-xs font-mono" />
          </FormItem>} />

      <FormField control={control} name="intended_use" rules={{
      required: "Intended use is required"
    }} render={({
      field
    }) => <FormItem className="relative z-0 w-full">
            <FormControl>
              <input {...field} className={inputClass} placeholder="Sale, Management, Tax, etc." />
            </FormControl>
            <FormLabel className={labelClass}>Intended Use</FormLabel>
            <FormMessage className="absolute -bottom-5 text-xs font-mono" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem className="relative z-0 w-full pt-2">
            <FormControl>
              <input {...field} type="email" className={inputClass} placeholder="email@domain.com" />
            </FormControl>
            <FormLabel className={labelClass}>Email Address</FormLabel>
            <FormMessage className="absolute -bottom-5 text-xs font-mono" />
          </FormItem>} />

      {submitError && <div className="flex items-center gap-2 rounded-sm bg-destructive/10 p-4 text-sm text-destructive font-mono">
          <CircleAlert className="size-4 shrink-0" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" variant="default" disabled={isSubmitting} className="w-full h-14 text-base font-default font-bold uppercase tracking-widest bg-[#FF9900] text-black hover:bg-[#E68A00] rounded-sm shadow-lg transition-transform active:scale-[0.98]">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Processing...
          </> : "Request Quote & Order"}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  return <section id="order-form" className="w-full bg-background text-foreground relative">
      <div className="grid min-h-[800px] grid-cols-1 lg:grid-cols-2">
        {}
        <div className="flex flex-col justify-center px-6 py-16 md:px-12 lg:px-24 xl:px-32">
          <div className="mx-auto w-full max-w-md lg:mx-0">
            <div className="mb-10">
              <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter text-foreground mb-4">
                Order Your TCA
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed font-default">
                Secure a defensible, LiDAR-backed Timber Cruise Analysis. Provide your stand details below to receive a custom quote and initiate your inventory scan.
              </p>
            </div>

            <FormProvider formKey="order_form_purchase" formId="order_form_purchase" sectionName="OrderForm" defaultValues={{
            name: "",
            acres: "",
            location: "",
            intended_use: "",
            email: ""
          }} validationRules={{
            name: {
              required: true
            },
            acres: {
              required: true
            },
            location: {
              required: true
            },
            intended_use: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            }
          }} submitText="Request Quote & Order" successMessage="Order request received. A GTMX analyst will review your acreage and contact you with a flight schedule and final quote within 24 hours." errorMessage="Transmission error. Please verify your connection and try again.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>

        {}
        <div className="hidden lg:block relative bg-secondary overflow-hidden">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645510/img/lidar_forest_scan_overlay.png" alt="LiDAR Point Cloud Forest Analysis" className="absolute inset-0 h-full w-full object-cover object-center opacity-80" loading="lazy" />
          {}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent mix-blend-multiply" />
          <div className="absolute inset-0 border-l border-border/20" />
        </div>
      </div>
    </section>;
}